import { Component, Inject, PLATFORM_ID } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { MessageModel, MultipleSectionsViewModel } from '@flocasts/experience-service-types'
import { BehaviorSubject, merge, mergeMap, Observable } from 'rxjs'
import { distinctUntilChanged, filter, map, shareReplay, skip, switchMap, tap, withLatestFrom } from 'rxjs/operators'
import { ResultsDataService } from '../results-data.service'
import { isEventCard, isMessageModel, isTabModel, notNullOrUndefined } from '../../shared/functions/type-guards'
import { matchingResultIndexQueryParams } from '../results.utility'
import { GeoBlockingService } from '../../singleton-services/geo-blocking.service'
import { WINDOW } from '../../app.injections'
import { isPlatformBrowser } from '@angular/common'
import {
  EXP_FLOWRESTLING_EVENT_HUB_BRACKETS_ID,
  EXP_FLOWRESTLING_EVENT_HUB_BRACKETS_VARIATION
} from 'src/app/shared/experimentation/experiment.model'
import { ExperimentationService } from 'src/app/shared/experimentation/experimentation.service'

/**
 * Collect data for child component from AR, search and pagination
 */
@Component({
  selector: 'flo-results-list',
  template: `
    <flo-results-list-view
      [tabItems]="tabItems$ | async"
      [resultsSections]="resultsSections$ | async"
      [messageModelSection]="messageModel$ | async"
      [geoBlockedIds]="geoBlockedIds$ | async"
      [searchTerm]="searchQueryParam$ | async"
      [isFilterOrSearchLoading]="isFilterOrSearchLoading$ | async"
      (search)="updateSearchQueryParam($event)"
    ></flo-results-list-view>
  `
})
export class ResultsListComponent {
  constructor(
    private readonly ar: ActivatedRoute,
    private readonly router: Router,
    private readonly resultsApi: ResultsDataService,
    private readonly geoBlockingService: GeoBlockingService,
    private readonly experimentationService: ExperimentationService,
    @Inject(WINDOW) private readonly window: Window,
    // tslint:disable-next-line:ban-types
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId)
  }
  private readonly isBrowser: boolean

  private initialSections$: Observable<MultipleSectionsViewModel> = this.ar.data.pipe(
    map(data => data.results),
    // in the resolver, we conditionally fetch the new bff results data for the beta component, based on experiment variation.
    // This allows us to use the experimentation service in a single place.
    filter(notNullOrUndefined),
    // scroll to top if initial page load
    tap(_ => {
      if (this.isBrowser) this.window.scroll(0, 0)
    }),
    shareReplay(1)
  )

  public isFilterOrSearchLoading$ = new BehaviorSubject<boolean>(false)

  public readonly resultsParams$ = this.ar.queryParamMap.pipe(
    distinctUntilChanged(matchingResultIndexQueryParams),
    shareReplay(1)
  )

  public readonly searchQueryParam$ = this.resultsParams$.pipe(
    map(queryParamMap => queryParamMap.get('search')),
    distinctUntilChanged()
  )

  public isInEventHubBracketsVariation$ = this.experimentationService.isInVariation(
    EXP_FLOWRESTLING_EVENT_HUB_BRACKETS_ID,
    EXP_FLOWRESTLING_EVENT_HUB_BRACKETS_VARIATION,
    true
  )

  /**
   * Accumulated Sections, combining activated route, search, and pagination data
   * TODO: Add Pagination to combineLatest()
   */
  private updatedSections$: Observable<MultipleSectionsViewModel> = this.resultsParams$.pipe(
    // This is intentional; we initially get data from the resolver instead to prevent duplicate calls
    skip(1),
    withLatestFrom(this.isInEventHubBracketsVariation$),
    tap(() => this.isFilterOrSearchLoading$.next(true)),
    switchMap(([queryParams, isInEventHubBracketsVariation]) => {
      // list filters here
      const search = queryParams.get('search') ?? undefined
      const date = queryParams.get('date') ?? undefined

      return this.resultsApi.getResultsIndexExperience(
        search,
        date,
        undefined,
        undefined,
        isInEventHubBracketsVariation
      )
    }),
    tap(() => this.isFilterOrSearchLoading$.next(false)),
    shareReplay(1)
  )

  /**
   * Accumulated Results (event) Sections from the Activated Route, Search, and Pagination
   * TODO: include pagination
   */
  private combinedSections$ = merge(this.initialSections$, this.updatedSections$).pipe(
    filter(notNullOrUndefined),
    map(results => results.sections),
    shareReplay(1)
  )

  public resultsSections$ = this.combinedSections$.pipe(map(section => section.filter(isEventCard)))

  public messageModel$: Observable<MessageModel | undefined> = this.combinedSections$.pipe(
    map(section => section.find(isMessageModel))
  )

  public tabItems$ = this.combinedSections$.pipe(
    map(section => section.find(isTabModel)),
    map(section => section?.items)
  )

  public geoBlockedIds$ = this.resultsSections$.pipe(
    mergeMap(sections => sections.map(section => section.items)),
    map(items => items.map(item => item.liveEventMetadata?.liveEventId).filter(notNullOrUndefined)),
    switchMap(liveEventIds => this.geoBlockingService.getGeoBlockedLiveEvents(liveEventIds)),
    map(geoRes => geoRes.map(item => item?.id))
  )

  /**
   * Update search query param based on user input to the search box.
   */
  public updateSearchQueryParam(searchTerm: string | null) {
    this.isFilterOrSearchLoading$.next(true)
    const queryParams = { search: searchTerm }
    this.router.navigate([], {
      queryParams,
      relativeTo: this.ar,
      queryParamsHandling: 'merge',
      replaceUrl: true
    })
    this.isFilterOrSearchLoading$.next(false)
  }
}
